import type { SessionSlot } from '@features/customization-session/types';

import { Check, CircleAlert, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';

import { cn } from '@shared/utils/cn';

import { SlotRow } from '@features/customization-session/components/slot-row';
import { groupByLocation } from '@features/customization-session/utils/session-status';

type Props = {
  slots: SessionSlot[];
  /** Which step is validated: the products are checked on it, and the title says where to go. */
  step: 'load' | 'store';
  disabled: boolean;
  onToggle: (slot: SessionSlot) => void;
  onRemove?: (slot: SessionSlot) => void;
  onFetchPdf?: (customization: number) => void;
  showVisuals?: boolean;
};

/** The products by location: where to take them from (to load the jig), or where to put them back. */
export const LocationSlots = ({ slots, step, disabled, onToggle, onRemove, onFetchPdf, showVisuals }: Props) => (
  <div className="flex flex-col gap-6">
    {groupByLocation(slots).map((group) => {
      const done = group.slots.filter((slot) => (step === 'load' ? slot.loaded : slot.stored)).length;
      const complete = done === group.slots.length;
      const unknown = !group.workshop;
      const ActionIcon = step === 'load' ? ArrowUpFromLine : ArrowDownToLine;

      return (
        <section
          aria-label={`${step === 'load' ? 'Récupérer dans' : 'Ranger dans'} ${group.workshop || 'un emplacement inconnu'}${group.box ? `, caisse ${group.box}` : ''}`}
          className="flex flex-col gap-2"
          key={group.key}
        >
          <div
            className={cn(
              'flex items-center gap-3 rounded-lg border-l-4 px-4 py-3 transition-colors',
              complete
                ? 'border-success bg-success/8'
                : unknown
                  ? 'border-warning bg-warning/8'
                  : 'border-primary bg-primary/8'
            )}
          >
            <div
              aria-hidden="true"
              className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-lg text-white',
                complete ? 'bg-success' : unknown ? 'bg-warning' : 'bg-primary'
              )}
            >
              {complete ? (
                <Check className="size-6" />
              ) : unknown ? (
                <CircleAlert className="size-6" />
              ) : (
                <ActionIcon className="size-6" />
              )}
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <span
                className={cn(
                  'text-xs font-semibold tracking-wide uppercase',
                  complete ? 'text-success' : unknown ? 'text-warning' : 'text-primary'
                )}
              >
                {step === 'load' ? 'Récupérer dans' : 'Ranger dans'}
              </span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-xl leading-tight font-bold [overflow-wrap:anywhere]">
                  {group.workshop || 'Emplacement inconnu'}
                </span>
                {group.box && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-foreground/20 bg-background px-2 py-0.5 text-base font-semibold">
                    <span className="text-xs font-medium text-muted-foreground uppercase">Caisse</span>
                    <span className="tabular-nums">{group.box}</span>
                  </span>
                )}
              </div>
            </div>

            <span
              className={cn(
                'shrink-0 text-base font-semibold tabular-nums',
                complete ? 'text-success' : 'text-muted-foreground'
              )}
            >
              {done} / {group.slots.length}
            </span>
          </div>

          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
            {group.slots.map((slot) => (
              <SlotRow
                checked={step === 'load' ? slot.loaded : slot.stored}
                disabled={disabled}
                key={slot.number}
                onFetchPdf={onFetchPdf}
                onRemove={onRemove && (() => onRemove(slot))}
                onToggle={() => onToggle(slot)}
                showVisuals={showVisuals}
                slot={slot}
              />
            ))}
          </div>
        </section>
      );
    })}
  </div>
);
